import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { FieldConfig, FieldType, SelectOption } from '../../interfaces/field-config.interface';

@Component({
  selector: 'app-field-renderer-user',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container #vcr></ng-container>
  `,
})
export class FieldRendererUserComponent implements OnInit, OnChanges, OnDestroy {

  @Input({ required: true }) config!: FieldConfig;
  @Input({ required: true }) control!: FormControl<any>;
  @Input() externalOptions: SelectOption[] = [];
  @Input() isDisabled: boolean = false;

  @ViewChild('vcr', { static: true, read: ViewContainerRef })
  vcr!: ViewContainerRef;

  private componentRef: ComponentRef<any> | null = null;
  private destroy$ = new Subject<void>();
  private initialized = false;

  constructor(private cdr: ChangeDetectorRef) {}

  async ngOnInit(): Promise<void> {
    if (this.config && this.control) {
      await this.renderComponent();
      this.initialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    // SOLO actualizar inputs, no recrear componente
    if (!this.initialized) return;

    if (
      changes['externalOptions'] &&
      this.componentRef &&
      (this.config?.type === 'select' || this.config?.type === 'multiselect')
    ) {
      this.componentRef.setInput('externalOptions', this.externalOptions);
    }

    if (changes['isDisabled'] && this.componentRef) {
      this.componentRef.instance?.setDisabledState?.(this.isDisabled);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyComponent();
  }

  private async renderComponent(): Promise<void> {

    if (!this.config || !this.control) return;
    if (this.config.visible === false) return;

    const type = this.config.type?.toLowerCase() as FieldType;

    const loader = {
      text: () => import('../../inputs/text-input/text-input.component').then(m => m.TextInputComponent),
      email: () => import('../../inputs/text-input/text-input.component').then(m => m.TextInputComponent),
      number: () => import('../../inputs/number-input/number-input.component').then(m => m.NumberInputComponent),
      select: () => import('../../inputs/select-input/select-input.component').then(m => m.SelectInputComponent),
      multiselect: () => import('../../inputs/select-input/select-input.component').then(m => m.SelectInputComponent),
      date: () => import('../../inputs/date-input/date-input.component').then(m => m.DateInputComponent),
      boolean: () => import('../../inputs/boolean-input/boolean-input.component').then(m => m.BooleanInputComponent),
      attachment: () => import('../../inputs/attachment-input/attachment-input.component').then(m => m.AttachmentInputComponent),
      form: () => import('../../inputs/nested-form-input/nested-form-input-user.component').then(m => m.NestedFormInputUserComponent),
    }[type];

    if (!loader) {
      console.warn('No loader for type:', type);
      return;
    }

    const componentType = await loader();

    this.componentRef = this.vcr.createComponent(componentType as Type<any>);

    this.componentRef.setInput('config', this.config);

    this.setupControlValueAccessor();
    this.setupValidator();

    this.cdr.markForCheck();
  }

  private setupControlValueAccessor(): void {
    if (!this.componentRef?.instance || !this.control) return;

    const instance = this.componentRef.instance;

    instance.writeValue?.(this.control.value);

    instance.registerOnChange?.((value: any) => {
      this.control.setValue(value);
      this.control.markAsTouched();
    });

    instance.registerOnTouched?.(() => {
      this.control.markAsTouched();
    });

    instance.setDisabledState?.(this.isDisabled || this.control.disabled);

    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        instance.writeValue?.(value);
      });
  }

  private setupValidator(): void {
    if (!this.componentRef?.instance || !this.control) return;

    const instance = this.componentRef.instance;

    if (typeof instance.validate !== 'function') return;

    const validatorFn = () => instance.validate(this.control);

    const existing = this.control.validator;

    this.control.setValidators(
      existing ? [existing, validatorFn] : validatorFn
    );

    this.control.updateValueAndValidity();
  }

  private destroyComponent(): void {
    this.componentRef?.destroy();
    this.componentRef = null;
    this.vcr?.clear();
  }
}